import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, XCircle, ArrowRight, Calendar, MapPin, DollarSign } from 'lucide-react';
import { paymentsAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [bookingInfo, setBookingInfo] = useState(null);
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id'); // For phone booking payments
  const pendingId = searchParams.get('pending_id'); // For guest checkout

  useEffect(() => {
    if (bookingId && sessionId) {
      // Phone booking payment - verify via booking endpoint
      verifyBookingPayment(bookingId, sessionId);
    } else if (pendingId && sessionId) {
      // Guest checkout - verify and create booking
      verifyGuestCheckout(sessionId, pendingId);
    } else if (sessionId) {
      // Fallback - try to find pending booking by session
      verifyBySessionOnly(sessionId);
    } else {
      setStatus('failed');
    }
  }, [sessionId, bookingId, pendingId]);

  const verifyGuestCheckout = async (sid, pId) => {
    try {
      const response = await api.get(`/checkout/verify/${sid}?pending_id=${pId}`);
      if (response.data.status === 'paid' || response.data.status === 'already_processed') {
        setBookingInfo({
          reference_number: response.data.reference_number,
          booking_id: response.data.booking_id,
          is_paid: true
        });
        setStatus('success');
      } else if (response.data.status === 'unpaid') {
        // Payment not complete, retry after delay
        setTimeout(() => {
          verifyGuestCheckout(sid, pId);
        }, 3000);
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Guest checkout verification error:', error);
      // If 404, the pending might have been processed already, show success
      if (error.response?.status === 404) {
        setStatus('success');
        setBookingInfo({ is_paid: true });
      } else {
        setStatus('failed');
      }
    }
  };

  const verifyBySessionOnly = async (sid, attempts = 0) => {
    // Try to find pending booking by stripe session ID and verify
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }
    
    try {
      // First try the pending booking lookup
      const pendingResponse = await api.get(`/checkout/pending-by-session/${sid}`);
      if (pendingResponse.data?.id) {
        verifyGuestCheckout(sid, pendingResponse.data.id);
        return;
      }
    } catch (e) {
      // If no pending found, try regular status check
    }
    
    try {
      const response = await paymentsAPI.getStatus(sid);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }
      setTimeout(() => verifyBySessionOnly(sid, attempts + 1), 2000);
    } catch (error) {
      setTimeout(() => verifyBySessionOnly(sid, attempts + 1), 2000);
    }
  };

  const verifyBookingPayment = async (bId, sId) => {
    try {
      const response = await api.get(`/checkout/verify-booking-payment/${bId}?session_id=${sId}`);
      if (response.data.is_paid) {
        setBookingInfo(response.data);
        setStatus('success');
      } else {
        // Retry a few times
        setTimeout(() => {
          api.get(`/checkout/verify-booking-payment/${bId}?session_id=${sId}`)
            .then(res => {
              if (res.data.is_paid) {
                setBookingInfo(res.data);
                setStatus('success');
              } else {
                setStatus('failed');
              }
            })
            .catch(() => setStatus('failed'));
        }, 3000);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('failed');
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await paymentsAPI.getStatus(sid);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[var(--primary)] animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold font-['Cormorant_Garamond'] mb-2">Verifying Payment...</h1>
          <p className="text-[var(--text-secondary)]">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)] p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card p-12 max-w-md text-center"
        >
          <div className="w-20 h-20 bg-[var(--error)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-[var(--error)]" />
          </div>
          <h1 className="text-2xl font-bold font-['Cormorant_Garamond'] mb-4">Payment Issue</h1>
          <p className="text-[var(--text-secondary)] mb-8">
            We couldn't verify your payment. Please check your booking status or try again.
          </p>
          <Link to="/customer/dashboard">
            <Button className="btn-primary">Go to Dashboard</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--background)] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card p-12 max-w-md text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="w-20 h-20 bg-[var(--success)]/10 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <Check className="w-10 h-10 text-[var(--success)]" />
        </motion.div>
        
        <h1 className="text-3xl font-bold font-['Cormorant_Garamond'] mb-4">Payment Successful!</h1>
        
        {bookingInfo ? (
          <>
            <p className="text-[var(--text-secondary)] mb-6">
              Thank you, {bookingInfo.customer_name}! Your payment has been received and your booking is confirmed.
            </p>
            
            {/* Booking Details */}
            <div className="bg-[var(--accent)] rounded-xl p-6 mb-6 text-left">
              <h3 className="font-semibold mb-4 text-center">Booking Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-[var(--text-muted)]">Reference:</span>
                  <span className="font-mono font-semibold text-[var(--primary)]">{bookingInfo.reference_number}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[var(--text-muted)]">Service:</span>
                  <span className="font-medium">{bookingInfo.service_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-[var(--text-muted)]" />
                  <span>{bookingInfo.scheduled_date} at {bookingInfo.scheduled_time}</span>
                </div>
                {bookingInfo.city && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[var(--text-muted)]" />
                    <span>{bookingInfo.city}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 pt-2 border-t border-[var(--border)]">
                  <DollarSign className="w-4 h-4 text-[var(--success)]" />
                  <span className="font-semibold text-[var(--success)]">${bookingInfo.total_amount?.toFixed(2)} PAID</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-[var(--text-secondary)] mb-8">
            Thank you for your booking! Your appointment has been confirmed. We'll assign a qualified 
            professional to your service and notify you with the details.
          </p>
        )}

        <div className="bg-[var(--accent)] rounded-xl p-6 mb-8 text-left">
          <h3 className="font-semibold mb-3">What happens next?</h3>
          <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-[var(--primary)] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
              A qualified worker will be assigned to your booking
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-[var(--primary)] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
              You'll receive a confirmation with worker details
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-[var(--primary)] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
              The worker will arrive at your scheduled time
            </li>
          </ul>
        </div>

        <Link to="/">
          <Button className="btn-primary">
            Return to Home
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
